import type { Store } from 'effector'
import type {
  CaptureExceptionParams,
  CaptureMessageParams,
  SentryCaptureExceptionParams,
  SentryCaptureMessageParams,
} from './index.h'

import * as Sentry from '@sentry/browser'
import { attach, createEvent, createStore, sample, scopeBind } from 'effector'
import { and } from 'patronum'
import { fullVersion, runtime } from '~/app/environment'
import { model as router } from '~/core/router'
import { model as config, env, remote } from '~/shared/config'
import { postpone } from '~/shared/effector'
import { history } from '~/shared/effector/belt'
import { T } from '~/shared/helpers'
import { COUNT_OF_PREVIOUS_ROUTES_IN_BREADCRUMBS } from './index.h'
import { isIgnoredError, logger, toBreadcrumbs } from './lib'

export const init = createEvent<void>()
export const captureException = createEvent<CaptureExceptionParams>()
export const captureMessage = createEvent<CaptureMessageParams>()

const awaited = createEvent<void>()

const $enabled: Store<boolean> = config.get(remote.tria_isSentryEnabled)
const $ignoreErrors: Store<string> = config.get(remote.tria_sentryIgnoreErrors)
const $isProduction = createStore(__NODE_ENV__ === 'production')
const $ready = createStore<boolean>(false)

const $locations = history({
  source: router.$location,
  size: COUNT_OF_PREVIOUS_ROUTES_IN_BREADCRUMBS,
}).map(toBreadcrumbs)

const getBreadcrumbsFx = attach({
  source: $locations,
  effect: (from) => [
    {
      type: 'navigation',
      category: 'navigation.stack',
      data: {
        from: from.length ? from : null,
        to: location.href,
      },
      level: 'info',
    } as Sentry.Breadcrumb,
  ],
})

const initSentryFx = attach({
  source: { errosToIgnore: $ignoreErrors },
  effect({ errosToIgnore }) {
    const getBreadcrumbs = scopeBind(getBreadcrumbsFx, { safe: true })

    // allows to setup ignored errors via firebase remote config
    const ignoreErrors: RegExp[] | undefined = errosToIgnore
      ? (errosToIgnore
          .split(';')
          .map((err) => (err ? new RegExp(err, 'i') : null))
          .filter(Boolean) as RegExp[])
      : undefined

    Sentry.init({
      dsn: env.TRIA_CLIENT_SENTRY_DSN,
      debug: env.TRIA_CLIENT_SENTRY_DEBUG,
      release: fullVersion,
      environment: env.TRIA_ENVIRONMENT,
      integrations: [
        Sentry.dedupeIntegration(),
        Sentry.httpContextIntegration(),
        Sentry.browserTracingIntegration(),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
        Sentry.thirdPartyErrorFilterIntegration({
          filterKeys: ['cu1raSBzaXR0IHNreWxpIHLh8HNub3RyYQ'], // https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-thirdpartyerrorfilterintegration
          // Possible values are:
          // - 'drop-error-if-contains-third-party-frames'
          // - 'drop-error-if-exclusively-contains-third-party-frames'
          // - 'apply-tag-if-contains-third-party-frames'
          // - 'apply-tag-if-exclusively-contains-third-party-frames'
          behaviour: 'drop-error-if-contains-third-party-frames',
        }),
      ],

      tracesSampleRate: 0.2,
      defaultIntegrations: false,

      ignoreErrors,

      beforeSend: async (event) => {
        if (isIgnoredError(event)) {
          return null
        }

        return {
          ...event,
          breadcrumbs: await getBreadcrumbs(),
        }
      },
    })

    Sentry.getCurrentScope().setTag('app.name', runtime.app.appName)
    Sentry.getCurrentScope().setTag('domain', location.origin)
  },
})

const captureExceptionFx = attach({
  source: $ready,
  effect: (ready, params: SentryCaptureExceptionParams) =>
    ready ? Sentry.captureException(...params) : undefined,
})

const captureMessageFx = attach({
  source: $ready,
  effect: (ready, params: SentryCaptureMessageParams) =>
    ready ? Sentry.captureMessage(...params) : undefined,
})

//
// init sentry
//

postpone({
  clock: init,
  until: config.$ready,
  target: awaited,
})

sample({
  clock: awaited,
  filter: and($isProduction, $enabled),
  target: initSentryFx,
})

sample({
  clock: initSentryFx.done,
  fn: T,
  target: $ready,
})

sample({
  clock: initSentryFx.done,
  fn: () => ['Sentry initialized successfully'],
  target: logger.debugFx,
})

sample({
  clock: initSentryFx.fail,
  fn: (error) => ['Sentry initialization error', error],
  target: logger.errorFx,
})

sample({
  clock: [captureExceptionFx.fail, captureMessageFx.fail],
  fn: (error) => ['Sentry failed to capture', error],
  target: logger.errorFx,
})

//
// capturing interface
//

sample({
  clock: captureException,
  fn: (params) =>
    (Array.isArray(params) ? params : [params]) as SentryCaptureExceptionParams,
  target: captureExceptionFx,
})

sample({
  clock: captureMessage,
  fn: (params) =>
    (Array.isArray(params) ? params : [params]) as SentryCaptureMessageParams,
  target: captureMessageFx,
})
